import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import RocketCard from "../components/RocketCard";
import Loading from "../components/Loading";
import { useAuth } from "../context/AuthContext";
import AdminContentForm, { AdminNewButton, AdminItemWrap, useAdminSection } from "../components/AdminContentForm";
import { Search } from "lucide-react";

export default function FleetPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const LIMIT = 24;

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: LIMIT, offset };
    if (q) params.search = q;
    api.get("/rockets", { params })
      .then(r => { setItems(r.data.results || []); setTotal(r.data.count || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, offset]);
  useEffect(() => { load(); }, [load]);

  const admin = useAdminSection("rocket", load);

  return (
    <div data-testid="page-fleet">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="sat-label sat-cyan">CHANNEL // 004</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">ROCKET FLEET</h1>
          <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">Complete vehicle registry — active, retired, and in-development launch systems.</p>
        </div>
        {isAdmin && <AdminNewButton section="rocket" label="Rocket" onClick={admin.openNew} />}
      </div>

      <div className="relative mb-6">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--sat-text-dim)]" />
        <input type="search" placeholder="SEARCH FLEET…" value={q}
          onChange={(e) => { setOffset(0); setQ(e.target.value); }}
          className="!pl-10" data-testid="fleet-search" />
      </div>

      {loading ? <Loading message="SYNCING FLEET REGISTRY" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map(r => (
              <AdminItemWrap key={r.id} item={r} isAdmin={isAdmin} onEdit={admin.openEdit} onDelete={admin.onDelete}>
                <RocketCard rocket={r} />
              </AdminItemWrap>
            ))}
          </div>
          {items.length === 0 && <div className="text-center py-16 sat-label">NO VEHICLES ON RECORD</div>}
          {total > LIMIT && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-[color:var(--sat-border)]">
              <button className="sat-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="fleet-prev">← PREV</button>
              <span className="sat-label">{offset + 1}–{Math.min(offset + LIMIT, total)} OF {total}</span>
              <button className="sat-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} data-testid="fleet-next">NEXT →</button>
            </div>
          )}
        </>
      )}

      {admin.formOpen && (
        <AdminContentForm
          section="rocket"
          initial={admin.editing}
          initialId={admin.editing?.admin_id}
          onClose={admin.closeForm}
          onSaved={admin.onSaved}
        />
      )}
    </div>
  );
}
